import React, { useCallback, useEffect, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { AlertCircle, CreditCard, Loader2, Shield } from 'lucide-react';

import { getStripeInstance } from '@/lib/stripe-loader';
import { Button } from '@/components/ui/button';
import { publicBookingV2Service, type BookingPaymentInfo } from '@/services/bookingService';
import { api } from '@/services/api';
import { cn } from '@/lib/utils';

interface PaymentStepProps {
  sessionToken: string;
  priceCents: number;
  stripeAccountId: string;
  onComplete: (paymentMethodId: string) => void;
  onSkip: () => void;
  /**
   * Which backend session flow to use:
   * - 'v2': OTP-verified V2 public booking (PatientActionLink, token in URL path)
   * - 'magic-link': PatientLinkSession via x-session-token header (BookingWidget magic links)
   * Defaults to 'v2'.
   */
  flow?: 'v2' | 'magic-link';
  /** Error surfaced from the parent after onComplete is awaited (e.g. card declined). */
  externalError?: string | null;
  /** True while the parent is processing the payment/booking. */
  isSubmitting?: boolean;
}

export const PaymentStep: React.FC<PaymentStepProps> = ({
  sessionToken,
  priceCents,
  stripeAccountId,
  onComplete,
  onSkip,
  flow = 'v2',
  externalError = null,
  isSubmitting: externalSubmitting = false,
}) => {
  // Pick the right backend endpoint pair for this session flow.
  const fetchPaymentInfo = React.useCallback(
    async (): Promise<BookingPaymentInfo> => {
      if (flow === 'magic-link') {
        const response = await api.get<{ success: boolean; data: BookingPaymentInfo }>(
          '/public/booking/payment-info',
          { headers: { 'x-session-token': sessionToken } },
        );
        // The magic-link controller wraps responses in { success, data }; the
        // api helper already unwraps the outer axios layer, so response === body.
        return (response as any).data ?? (response as unknown as BookingPaymentInfo);
      }
      return publicBookingV2Service.getPaymentInfo(sessionToken);
    },
    [flow, sessionToken],
  );


  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentInfo, setPaymentInfo] = useState<BookingPaymentInfo | null>(null);
  const [showAddCard, setShowAddCard] = useState(false);
  const [selectedPmId, setSelectedPmId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const info = await fetchPaymentInfo();
        if (cancelled) return;
        if (!info.requires_payment) {
          onSkip();
          return;
        }
        setPaymentInfo(info);
        if (info.payment_methods.length > 0) {
          setSelectedPmId(info.payment_methods[0].id);
        } else {
          setShowAddCard(true);
        }
      } catch (e) {
        if (!cancelled) {
          setError('Unable to load payment information. Please try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [sessionToken, onSkip, fetchPaymentInfo]);

  const handlePayWithSaved = useCallback(async () => {
    if (!selectedPmId) return;
    setSubmitting(true);
    onComplete(selectedPmId);
  }, [selectedPmId, onComplete]);

  // Reset the local submitting flag when the parent surfaces an error (e.g.
  // card declined). Otherwise the Pay button stays stuck in "Processing…".
  useEffect(() => {
    if (externalError) setSubmitting(false);
  }, [externalError]);

  const effectiveSubmitting = submitting || externalSubmitting;

  const formattedPrice = `$${(priceCents / 100).toFixed(2)}`;

  if (loading) {
    return (
      <div className='flex flex-col items-center justify-center py-16'>
        <Loader2 className='h-8 w-8 animate-spin text-teal-600 mb-3' />
        <p className='text-sm text-slate-500'>Loading payment details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='rounded-xl border border-red-200 bg-red-50 p-6 text-center'>
        <AlertCircle className='mx-auto h-8 w-8 text-red-500 mb-2' />
        <p className='text-sm text-red-700'>{error}</p>
      </div>
    );
  }

  const accountId = paymentInfo?.stripe_account_id || stripeAccountId;

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='text-center'>
        <div className='mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100'>
          <CreditCard className='h-6 w-6 text-emerald-600' />
        </div>
        <h2 className='text-xl font-semibold text-slate-900'>Payment Required</h2>
        <p className='mt-1 text-3xl font-bold text-slate-900'>{formattedPrice}</p>
        <p className='mt-1 text-sm text-slate-500'>
          Your card will be charged when the appointment is confirmed.
        </p>
      </div>

      {/* Saved cards OR add-card form */}
      {!showAddCard && paymentInfo && paymentInfo.payment_methods.length > 0 ? (
        <div className='space-y-4'>
          <p className='text-sm font-medium text-slate-700'>Select a payment method</p>
          <div className='space-y-2'>
            {paymentInfo.payment_methods.map((pm) => (
              <button
                key={pm.id}
                type='button'
                onClick={() => setSelectedPmId(pm.id)}
                className={cn(
                  'w-full flex items-center gap-3 rounded-lg border p-3 text-left transition-colors',
                  selectedPmId === pm.id
                    ? 'border-teal-500 bg-teal-50 ring-1 ring-teal-500'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50',
                )}
              >
                <div className='flex h-8 w-8 items-center justify-center rounded bg-slate-100'>
                  <CreditCard className='h-4 w-4 text-slate-600' />
                </div>
                <div className='flex-1'>
                  <p className='text-sm font-medium text-slate-900 capitalize'>
                    {pm.brand} &bull;&bull;&bull;&bull; {pm.last4}
                  </p>
                  <p className='text-xs text-slate-500'>
                    Exp {String(pm.exp_month).padStart(2, '0')}/{pm.exp_year}
                  </p>
                </div>
                <div
                  className={cn(
                    'h-4 w-4 rounded-full border-2',
                    selectedPmId === pm.id
                      ? 'border-teal-500 bg-teal-500'
                      : 'border-slate-300',
                  )}
                >
                  {selectedPmId === pm.id && (
                    <div className='h-full w-full flex items-center justify-center'>
                      <div className='h-1.5 w-1.5 rounded-full bg-white' />
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>

          {externalError && (
            <div className='rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-start gap-2'>
              <AlertCircle className='h-4 w-4 mt-0.5 shrink-0' />
              <div>
                <p className='font-medium'>Payment failed</p>
                <p>{externalError}</p>
              </div>
            </div>
          )}

          <Button
            onClick={handlePayWithSaved}
            disabled={!selectedPmId || effectiveSubmitting}
            className='w-full'
            size='lg'
          >
            {effectiveSubmitting ? (
              <>
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                Processing...
              </>
            ) : (
              `Pay ${formattedPrice}`
            )}
          </Button>

          <button
            type='button'
            onClick={() => setShowAddCard(true)}
            className='w-full text-center text-sm text-teal-600 hover:text-teal-700 hover:underline'
          >
            Use a different card
          </button>
        </div>
      ) : (
        <div className='space-y-4'>
          {paymentInfo && paymentInfo.payment_methods.length > 0 && (
            <button
              type='button'
              onClick={() => setShowAddCard(false)}
              className='text-sm text-teal-600 hover:text-teal-700 hover:underline'
            >
              &larr; Back to saved cards
            </button>
          )}
          {accountId && (
            <SetupIntentCardForm
              sessionToken={sessionToken}
              connectedAccountId={accountId}
              priceCents={priceCents}
              onComplete={onComplete}
              flow={flow}
            />
          )}
        </div>
      )}

      {/* Trust signal */}
      <div className='flex items-center justify-center gap-1.5 text-xs text-slate-400'>
        <Shield className='h-3 w-3' />
        <span>Secured by Stripe</span>
      </div>
    </div>
  );
};

// ── Inner Stripe Elements form for SetupIntent ────────────────────────────

interface SetupIntentCardFormProps {
  sessionToken: string;
  connectedAccountId: string;
  priceCents: number;
  onComplete: (paymentMethodId: string) => void;
  flow: 'v2' | 'magic-link';
}

const SetupIntentCardForm: React.FC<SetupIntentCardFormProps> = ({
  sessionToken,
  connectedAccountId,
  priceCents,
  onComplete,
  flow,
}) => {
  const [stripePromise] = useState(() => getStripeInstance(connectedAccountId));
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        let result: { client_secret: string; stripe_account_id: string };
        if (flow === 'magic-link') {
          const response = await api.post<{
            success: boolean;
            data: { client_secret: string; stripe_account_id: string };
          }>(
            '/public/booking/setup-intent',
            {},
            { headers: { 'x-session-token': sessionToken } },
          );
          result = ((response as any).data ?? (response as any)) as {
            client_secret: string;
            stripe_account_id: string;
          };
        } else {
          result = await publicBookingV2Service.createSetupIntent(sessionToken);
        }
        if (!cancelled) {
          setClientSecret(result.client_secret);
        }
      } catch (err) {
        console.error('[PaymentStep] SetupIntent init failed:', err);
        if (!cancelled) setError('Could not initialize card form. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    init();
    return () => { cancelled = true; };
  }, [sessionToken, flow]);

  if (loading) {
    return (
      <div className='space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4'>
        <div className='h-4 w-1/3 animate-pulse rounded bg-slate-200' />
        <div className='h-10 w-full animate-pulse rounded bg-slate-200' />
        <div className='h-10 w-full animate-pulse rounded bg-slate-200' />
      </div>
    );
  }

  if (error || !clientSecret) {
    return (
      <div className='rounded-lg border border-red-200 bg-red-50 p-4 text-center'>
        <AlertCircle className='mx-auto h-6 w-6 text-red-500 mb-1' />
        <p className='text-sm text-red-700'>{error || 'Unable to load card form.'}</p>
      </div>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#0d9488',
            borderRadius: '8px',
          },
        },
      }}
    >
      <SetupIntentFormInner priceCents={priceCents} onComplete={onComplete} />
    </Elements>
  );
};

interface SetupIntentFormInnerProps {
  priceCents: number;
  onComplete: (paymentMethodId: string) => void;
}

const SetupIntentFormInner: React.FC<SetupIntentFormInnerProps> = ({
  priceCents,
  onComplete,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isElementReady, setIsElementReady] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const formattedPrice = `$${(priceCents / 100).toFixed(2)}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsSubmitting(true);
    setFormError(null);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setFormError(submitError.message ?? 'Validation failed');
      setIsSubmitting(false);
      return;
    }

    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
    });

    if (error) {
      setFormError(error.message ?? 'Unable to save card');
      setIsSubmitting(false);
      return;
    }

    if (setupIntent?.payment_method) {
      const pmId = typeof setupIntent.payment_method === 'string'
        ? setupIntent.payment_method
        : setupIntent.payment_method.id;
      onComplete(pmId);
    } else {
      setFormError('Could not save payment method. Please try again.');
    }
    setIsSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className='space-y-4'>
      <div className='relative'>
        {!isElementReady && (
          <div className='space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4'>
            <div className='h-4 w-1/3 animate-pulse rounded bg-slate-200' />
            <div className='h-10 w-full animate-pulse rounded bg-slate-200' />
            <div className='h-10 w-full animate-pulse rounded bg-slate-200' />
          </div>
        )}
        <div className={!isElementReady ? 'absolute inset-0 opacity-0' : ''}>
          <PaymentElement onReady={() => setIsElementReady(true)} />
        </div>
      </div>

      {formError && (
        <div className='rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700 flex items-start gap-2'>
          <AlertCircle className='h-4 w-4 mt-0.5 flex-shrink-0' />
          <span>{formError}</span>
        </div>
      )}

      <Button
        type='submit'
        className='w-full'
        size='lg'
        disabled={!stripe || !elements || isSubmitting}
      >
        {isSubmitting ? (
          <>
            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            Processing...
          </>
        ) : (
          `Pay ${formattedPrice}`
        )}
      </Button>
    </form>
  );
};

export default PaymentStep;
